import React from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Skeleton, IconButton, Container, Stack, useMediaQuery, useTheme, Pagination, Avatar } from '@mui/material';
import { getLogoUrl, getFundInitial } from '../../utils/getLogoUrl';
import { ChevronLeft, ChevronRight, FirstPage, LastPage } from "@mui/icons-material";

// Custom MUI Components
import {
    AppBoxComponent as Box,
    AppTypographyComponent as Typography,
    Grid,
    AppTableComponent as Table,
    AppTableBody as TableBody,
    AppTableCell as TableCell,
    AppTableContainer as TableContainer,
    AppTableHead as TableHead,
    AppTableRow as TableRow,
    AppSelectComponent as Select,
} from '../../components/mui';
import MenuItem from '../../components/mui/MenuItem';

import {
    useGetInvestmentPortfolioFDRDQuery,
    useGetInvestmentPortfolioFDRDDetailsQuery
} from '../../store/api/finfactor.apislice';

import ROUTES from '../../router/routes.constants';
import NodisplayComponent from "../../components/common/NodisplayComponent";
import CircleIcon from "../../assets/circleicon.svg";
import { colors } from "../../theme";

/* ---------------- COMMON STYLES ---------------- */

const glassCard = {
    backgroundColor: colors.dark.background.card,
    borderRadius: "24px",
    border: `1px solid ${colors.dark.stroke.divider}`,
    boxShadow: "0 4px 30px rgba(0, 0, 0, 0.3)"
};

const labelStyle = {
    fontSize: { xs: "13px", sm: "13px" },
    color: colors.dark.text.paragraph,
    fontWeight: 400,
    whiteSpace: "nowrap",
    flexShrink: 0
};

const valueStyle = {
    fontSize: "13px",
    color: colors.dark.text.heading,
    fontWeight: 600,
    textAlign: { xs: "right", sm: "left" },
    wordBreak: "break-word"
};

const detailRowStack = {
    direction: "row",
    justifyContent: { xs: "space-between", sm: "flex-start" },
    spacing: { xs: 0, sm: 0.5 },
    alignItems: "center",
    width: "100%",
    py: { xs: 1.5, sm: 0 },
    borderBottom: { xs: `1px solid ${colors.dark.stroke.divider}`, sm: "none" }
};

/* ---------------- CARD ---------------- */

const DepositCard = ({ deposit, onClick, isClickable = true }) => {
    return (
        <Box
            onClick={isClickable ? onClick : undefined}
            sx={{
                ...glassCard,
                mb: 2.5,
                p: 0,
                overflow: "hidden",
                cursor: isClickable ? "pointer" : "default",
                transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
            }}
        >
            {/* Row 1: Logo + Account Number + Date | Investment Amount */}
            <Box sx={{ backgroundColor: "#25293B", p: { xs: 2, md: 2.5 } }}>
                <Stack
                    direction={{ xs: "column", sm: "row" }}
                    justifyContent="space-between"
                    alignItems={{ xs: "flex-start", sm: "center" }}
                    spacing={2}
                >
                    <Stack direction="row" spacing={1.5} alignItems="center">
                        <Avatar
                            src={getLogoUrl(deposit)}
                            variant="rounded"
                            sx={{
                                width: 40, height: 40, borderRadius: "8px",
                                bgcolor: "#fff", p: 0.5,
                                display: "flex", alignItems: "center", justifyContent: "center",
                                overflow: "hidden", flexShrink: 0,
                                fontSize: "16px", fontWeight: 700, color: "#1B1F31"
                            }}
                        >
                            {getFundInitial(deposit)}
                        </Avatar>
                        <Stack direction={{ xs: "column", sm: "row" }} spacing={{ xs: 0, sm: 1.5 }} alignItems={{ xs: "flex-start", sm: "center" }}>
                            <Typography sx={{ color: colors.dark.text.heading, fontSize: "14px", fontWeight: 700 }}>
                                {deposit.accountNumber}
                            </Typography>
                            <Typography sx={{ color: colors.dark.text.paragraph, fontSize: "12px", fontWeight: 400 }}>
                                {deposit.asOnDateText}
                            </Typography>
                        </Stack>
                    </Stack>

                    {/* Investment Amount - right side */}
                    <Stack
                        direction={{ xs: "column", sm: "row" }}
                        spacing={{ xs: 0, sm: 0.5 }}
                        alignItems={{ xs: "flex-start", sm: "center" }}
                        sx={{ width: { xs: '100%', sm: 'auto' }, justifyContent: { xs: 'flex-start', sm: 'flex-end' } }}
                    >
                        <Typography sx={labelStyle}>Investment Amount :</Typography>
                        <Typography sx={{ color: colors.primary.main, fontSize: { xs: "16px", sm: "14px" }, fontWeight: 700 }}>
                            {deposit.investmentAmount}
                        </Typography>
                    </Stack>
                </Stack>
            </Box>

            {/* Divider */}
            <Box sx={{ borderBottom: `1px solid ${colors.dark.stroke.divider}` }} />

            {/* Row 2: Details in 2-column layout matching Figma */}
            <Box sx={{ backgroundColor: "#1B1F31", p: { xs: 2, md: 2.5 } }}>
                <Grid container spacing={1.5}>
                    {/* Row 1: Pay Frequency | ROI */}
                    <Grid item xs={12} sm={6}>
                        <Stack {...detailRowStack}>
                            <Typography sx={labelStyle}>Pay Frequency :</Typography>
                            <Typography sx={valueStyle}>{deposit.payFrequency}</Typography>
                        </Stack>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <Stack {...detailRowStack} justifyContent={{ xs: "space-between", sm: "flex-end" }}>
                            <Typography sx={labelStyle}>ROI (p.a.) :</Typography>
                            <Typography sx={valueStyle}>{deposit.roi}</Typography>
                        </Stack>
                    </Grid>

                    {/* Row 2: Payout Amount | Maturity Amount */}
                    <Grid item xs={12} sm={6}>
                        <Stack {...detailRowStack}>
                            <Typography sx={labelStyle}>Payout Amount :</Typography>
                            <Typography sx={valueStyle}>{deposit.payoutAmount}</Typography>
                        </Stack>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <Stack {...detailRowStack} justifyContent={{ xs: "space-between", sm: "flex-end" }}>
                            <Typography sx={labelStyle}>Maturity Amount :</Typography>
                            <Typography sx={valueStyle}>{deposit.maturityAmount}</Typography>
                        </Stack>
                    </Grid>

                    {/* Row 3: Maturity Date */}
                    <Grid item xs={12}>
                        <Stack {...detailRowStack} sx={{ borderBottom: "none" }}>
                            <Typography sx={labelStyle}>Maturity Date :</Typography>
                            <Typography sx={valueStyle}>{deposit.maturityDate}</Typography>
                        </Stack>
                    </Grid>
                </Grid>
            </Box>
        </Box>
    );
};

/* ---------------- MAIN CONTAINER ---------------- */

const Finfdrdcontainer = () => {
    const { category, brokerId } = useParams();
    const navigate = useNavigate();
    const location = useLocation();

    const { data: listData, isLoading: isListLoading, isError: isListError } =
        useGetInvestmentPortfolioFDRDQuery(undefined, { skip: !!brokerId });

    const { data: detailData, isLoading: isDetailLoading, isError: isDetailError } =
        useGetInvestmentPortfolioFDRDDetailsQuery(brokerId, { skip: !brokerId });

    const [page, setPage] = React.useState(1);
    const [rowsPerPage, setRowsPerPage] = React.useState(10);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

    // Sync account number with breadcrumbs on refresh
    React.useEffect(() => {
        if (detailData?.accountNumber && !location.state?.brokerName) {
            navigate(location.pathname, {
                replace: true,
                state: { ...location.state, brokerName: detailData.accountNumber }
            });
        }
    }, [detailData, location.state, navigate, location.pathname]);

    const handleClick = (id, accountNumber) => {
        navigate(ROUTES.INVESTMENT_PORTFOLIO_BROKER
            .replace(":category", category)
            .replace(":brokerId", id),
            { state: { brokerName: accountNumber } }
        );
    };

    if (isListLoading || isDetailLoading) {
        return (
            <Container maxWidth="lg" sx={{ mt: 4 }}>
                <Skeleton variant="rectangular" height={160} sx={{ borderRadius: "24px", mb: 4, bgcolor: "rgba(255,255,255,0.05)" }} />
                <Skeleton variant="rectangular" height={400} sx={{ borderRadius: "24px", bgcolor: "rgba(255,255,255,0.05)" }} />
            </Container>
        );
    }

    if (isListError || isDetailError || (!listData && !brokerId) || (!detailData && brokerId)) {
        return (
            <Box sx={{ minHeight: "100vh", display: "flex", justifyContent: "center", alignItems: "center" }}>
                <NodisplayComponent title="No Data Found" subtitle="We couldn't retrieve the records at this time." />
            </Box>
        );
    }

    /* ---------------- DETAIL PAGE ---------------- */
    if (brokerId && detailData) {
        const transactions = detailData.transactions || [];
        const totalRows = transactions.length;
        const displayedRows = transactions.slice((page - 1) * rowsPerPage, page * rowsPerPage);

        return (
            <Container maxWidth="lg" sx={{ mt: 2, pb: 4 }}>
                {/* Header Summary Card — same layout as DepositCard */}
                <Box sx={{ ...glassCard, p: 0, mb: 4, overflow: "hidden" }}>
                    {/* Row 1: Logo + Account Number | Investment Amount */}
                    <Box sx={{ backgroundColor: "#25293B", p: { xs: 2.5, md: 3 } }}>
                        <Stack
                            direction={{ xs: "column", sm: "row" }}
                            justifyContent="space-between"
                            alignItems={{ xs: "flex-start", sm: "center" }}
                            spacing={2}
                        >
                            <Stack direction="row" spacing={1.5} alignItems="center">
                                <Avatar
                                    src={getLogoUrl(detailData)}
                                    variant="rounded"
                                    sx={{
                                        width: 44, height: 44, borderRadius: "10px",
                                        bgcolor: "#fff", p: 0.5,
                                        display: "flex", alignItems: "center", justifyContent: "center",
                                        overflow: "hidden", flexShrink: 0,
                                        fontSize: "18px", fontWeight: 700, color: "#1B1F31"
                                    }}
                                >
                                    {getFundInitial(detailData)}
                                </Avatar>
                                <Typography sx={{ color: colors.dark.text.heading, fontSize: { xs: "16px", sm: "18px" }, fontWeight: 700 }}>
                                    {detailData.accountNumber}
                                </Typography>
                            </Stack>

                            <Stack
                                direction={{ xs: "column", sm: "row" }}
                                spacing={{ xs: 0, sm: 0.5 }}
                                alignItems={{ xs: "flex-start", sm: "center" }}
                                sx={{ width: { xs: '100%', sm: 'auto' }, justifyContent: { xs: 'flex-start', sm: 'flex-end' } }}
                            >
                                <Typography sx={{ ...labelStyle, color: colors.primary.main, fontWeight: 500 }}>Investment Amount :</Typography>
                                <Typography sx={{ color: colors.primary.main, fontSize: { xs: "20px", sm: "18px" }, fontWeight: 700 }}>
                                    {detailData.investmentAmount}
                                </Typography>
                            </Stack>
                        </Stack>
                    </Box>

                    {/* Divider */}
                    <Box sx={{ borderBottom: `1px solid ${colors.dark.stroke.divider}` }} />

                    {/* Row 2: Details - 2 column layout matching DepositCard */}
                    <Box sx={{ backgroundColor: "#1B1F31", p: { xs: 2.5, md: 3 } }}>
                        <Grid container spacing={1.5}>
                            <Grid item xs={12} sm={6}>
                                <Stack {...detailRowStack}>
                                    <Typography sx={labelStyle}>Pay Frequency :</Typography>
                                    <Typography sx={valueStyle}>{detailData.payFrequency}</Typography>
                                </Stack>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <Stack {...detailRowStack} justifyContent={{ xs: "space-between", sm: "flex-end" }}>
                                    <Typography sx={labelStyle}>ROI (p.a.) :</Typography>
                                    <Typography sx={valueStyle}>{detailData.roi}</Typography>
                                </Stack>
                            </Grid>

                            <Grid item xs={12} sm={6}>
                                <Stack {...detailRowStack}>
                                    <Typography sx={labelStyle}>Payout Amount :</Typography>
                                    <Typography sx={valueStyle}>{detailData.payoutAmount}</Typography>
                                </Stack>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <Stack {...detailRowStack} justifyContent={{ xs: "space-between", sm: "flex-end" }}>
                                    <Typography sx={labelStyle}>Maturity Amount :</Typography>
                                    <Typography sx={valueStyle}>{detailData.maturityAmount}</Typography>
                                </Stack>
                            </Grid>

                            <Grid item xs={12}>
                                <Stack {...detailRowStack} sx={{ borderBottom: "none" }}>
                                    <Typography sx={labelStyle}>Maturity Date :</Typography>
                                    <Typography sx={valueStyle}>{detailData.maturityDate}</Typography>
                                </Stack>
                            </Grid>
                        </Grid>
                    </Box>
                </Box>

                {/* Transaction history section */}
                <Box sx={{ ...glassCard, p: { xs: 2, md: 4 } }}>
                    <Typography sx={{ color: colors.dark.text.heading, mb: 3, fontSize: "18px", fontWeight: 600 }}>Transaction history</Typography>

                    <TableContainer sx={{ border: `1px solid ${colors.dark.stroke.divider}`, borderRadius: "12px", overflowX: "auto" }}>
                        <Table sx={{ minWidth: 600 }}>
                            <TableHead>
                                <TableRow sx={{ backgroundColor: "rgba(255,255,255,0.05)" }}>
                                    {["Date", "Time", "Description", "Amount (₹)"].map((head, i) => (
                                        <TableCell key={i} sx={{
                                            borderBottom: `1px solid ${colors.dark.stroke.divider}`,
                                            borderRight: i !== 3 ? `1px solid ${colors.dark.stroke.divider}` : 'none',
                                            color: colors.dark.text.paragraph,
                                            fontSize: "13px",
                                            fontWeight: 400,
                                            textAlign: "left"
                                        }}>
                                            {head}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {displayedRows.map((tx, idx) => (
                                    <TableRow key={idx} sx={{ backgroundColor: idx % 2 === 0 ? "transparent" : "rgba(255,255,255,0.02)" }}>
                                        <TableCell sx={{
                                            borderBottom: `1px solid ${colors.dark.stroke.divider}`,
                                            borderRight: `1px solid ${colors.dark.stroke.divider}`,
                                            color: "#E5E7EB",
                                            fontSize: "13px",
                                            fontWeight: 400
                                        }}>{tx.date}</TableCell>
                                        <TableCell sx={{
                                            borderBottom: `1px solid ${colors.dark.stroke.divider}`,
                                            borderRight: `1px solid ${colors.dark.stroke.divider}`,
                                            color: "#E5E7EB",
                                            fontSize: "13px",
                                            fontWeight: 400
                                        }}>{typeof tx.time === 'string' ? tx.time.toUpperCase() : tx.time}</TableCell>
                                        <TableCell sx={{
                                            borderBottom: `1px solid ${colors.dark.stroke.divider}`,
                                            borderRight: `1px solid ${colors.dark.stroke.divider}`,
                                            color: "#E5E7EB",
                                            fontSize: "13px",
                                            fontWeight: 400
                                        }}>{tx.description}</TableCell>
                                        <TableCell sx={{
                                            borderBottom: `1px solid ${colors.dark.stroke.divider}`,
                                            color: "#E5E7EB",
                                            fontSize: "13px",
                                            fontWeight: 400,
                                            textAlign: "left"
                                        }}>{tx.amount}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>

                    {/* Pagination */}
                    <Stack
                        direction={{ xs: "column", sm: "row" }}
                        alignItems="center"
                        justifyContent="space-between"
                        sx={{ mt: 3, pt: 2, gap: { xs: 3, sm: 0 } }}
                    >
                        <Typography sx={{ color: "rgba(255,255,255,0.4)", fontSize: "13px" }}>
                            {(page - 1) * rowsPerPage + 1}-{Math.min(page * rowsPerPage, totalRows)} of {totalRows} rows
                        </Typography>
                        <Pagination
                            count={Math.ceil(totalRows / rowsPerPage)}
                            page={page}
                            onChange={(e, p) => setPage(p)}
                            siblingCount={isMobile ? 0 : 1}
                            boundaryCount={isMobile ? 1 : 1}
                            size={isMobile ? "small" : "medium"}
                            showFirstButton={!isMobile}
                            showLastButton={!isMobile}
                            shape="rounded"
                            sx={{
                                "& .MuiPaginationItem-root": {
                                    color: "rgba(255, 255, 255, 0.6)",
                                    backgroundColor: "transparent",
                                    border: "1px solid rgba(255, 255, 255, 0.2)",
                                    borderRadius: "6px",
                                    minWidth: { xs: "32px", md: "36px" },
                                    height: { xs: "32px", md: "36px" },
                                    "&:hover": {
                                        backgroundColor: "rgba(255, 255, 255, 0.1)",
                                    },
                                    "&.Mui-selected": {
                                        backgroundColor: "#55B4E3",
                                        color: "white",
                                        border: "none",
                                        "&:hover": {
                                            backgroundColor: "#55B4E3",
                                        }
                                    },
                                    "& .MuiPaginationItem-icon": {
                                        fontSize: { xs: "18px", md: "20px" },
                                        color: "rgba(255, 255, 255, 0.6)"
                                    }
                                }
                            }}
                        />

                        <Select
                            value={rowsPerPage}
                            onChange={(e) => { setRowsPerPage(e.target.value); setPage(1); }}
                            size="small"
                            MenuProps={{
                                PaperProps: {
                                    sx: {
                                        backgroundColor: "#13172a",
                                        color: "white",
                                        border: "1px solid rgba(255, 255, 255, 0.1)",
                                        mt: 0.5,
                                        "& .MuiMenuItem-root": {
                                            fontSize: "13px",
                                            "&:hover": {
                                                backgroundColor: "rgba(255, 255, 255, 0.05)",
                                            },
                                            "&.Mui-selected": {
                                                backgroundColor: "rgba(255, 255, 255, 0.1)",
                                                color: "#55B4E3",
                                                "&:hover": {
                                                    backgroundColor: "rgba(255, 255, 255, 0.15)",
                                                }
                                            }
                                        }
                                    }
                                }
                            }}
                            sx={{
                                ml: { xs: 0, sm: 2 },
                                height: 36,
                                color: "#fff",
                                bgcolor: "rgba(255,255,255,0.03)",
                                borderRadius: "8px",
                                fontSize: "13px",
                                "& .MuiOutlinedInput-notchedOutline": {
                                    borderColor: "rgba(255,255,255,0.1)"
                                },
                                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                                    borderColor: "#55B4E3",
                                    borderWidth: "1.5px"
                                },
                                "& .MuiSvgIcon-root": { color: "#fff" }
                            }}
                        >
                            <MenuItem value={10}>10 per page</MenuItem>
                            <MenuItem value={20}>20 per page</MenuItem>
                            <MenuItem value={50}>50 per page</MenuItem>
                        </Select>
                    </Stack>
                </Box>
            </Container>
        );
    }

    /* ---------------- LIST PAGE ---------------- */
    return (
        <Container maxWidth="lg" sx={{ mt: 2, pb: 4 }}>
            {[listData.fd, listData.rd].map((section, idx) => (
                <Box key={idx} sx={{ ...glassCard, p: { xs: 2.5, md: 4 }, mb: 4 }}>
                    <Stack
                        direction={{ xs: "column", sm: "row" }}
                        justifyContent="space-between"
                        alignItems={{ xs: "flex-start", sm: "center" }}
                        sx={{ mb: 4, gap: 2 }}
                    >
                        <Stack direction="row" spacing={2} alignItems="center">
                            <Box component="img" src={CircleIcon} sx={{ width: 28, height: 28 }} />
                            <Typography sx={{ color: colors.dark.text.heading, fontSize: { xs: "18px", sm: "20px" }, fontWeight: 700 }}>
                                {section.title}
                            </Typography>
                        </Stack>
                        <Typography sx={{ color: colors.dark.text.heading, fontSize: { xs: "20px", sm: "24px" }, fontWeight: 700, fontFamily: "'Outfit', sans-serif" }}>
                            {section.totalValue}
                        </Typography>
                    </Stack>

                    <Box>
                        {(section.deposits || []).map(dep => (
                            <DepositCard
                                key={dep.id}
                                deposit={dep}
                                onClick={() => handleClick(dep.id, dep.accountNumber)}
                                isClickable={idx === 1} // Index 0 is FD, Index 1 is RD
                            />
                        ))}
                    </Box>
                </Box>
            ))}
        </Container>
    );
};

export default Finfdrdcontainer;